import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Badge,
} from '@mui/material';
import {
  Person as PersonIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

const AbsenceOverview = () => {
  // Fonction pour convertir Timestamp en date lisible
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Invalid Date';
    if (timestamp instanceof Timestamp) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    // Si c'est déjà un objet Date
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('fr-FR');
    }
    // Si c'est un objet avec seconds
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    return 'Invalid Date';
  };
  const { currentUser, userData } = useAuth();
  const [currentAbsences, setCurrentAbsences] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approuvé':
        return 'success';
      case 'en attente':
        return 'warning';
      case 'refusé':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approuvé':
        return 'Approuvée';
      case 'en attente':
        return 'En attente';
      case 'refusé':
        return 'Refusée';
      default:
        return 'Inconnue';
    }
  };

  const fetchAbsencesAndRequests = async () => {
    try {
      setLoading(true);
      
      if (!currentUser?.uid) {
        return;
      }

      // Récupérer les absences en cours
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Récupérer les absences
      const absencesQuery = query(
        collection(db, 'absences'),
        where('chefServiceId', '==', currentUser.uid)
      );
      
      const absencesSnapshot = await getDocs(absencesQuery);
      
      // Traiter les absences
      const allAbsences = absencesSnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Vérifier si les dates sont présentes
        if (!data.dateDebut || !data.dateFin) {
          return null;
        }
        
        try {
          let startDate, endDate;
          
          // Convertir les dates selon leur format
          if (data.dateDebut instanceof Timestamp) {
            startDate = data.dateDebut.toDate();
          } else if (typeof data.dateDebut === 'string') {
            startDate = new Date(data.dateDebut);
          } else if (data.dateDebut.seconds) {
            startDate = new Date(data.dateDebut.seconds * 1000);
          }

          if (data.dateFin instanceof Timestamp) {
            endDate = data.dateFin.toDate();
          } else if (typeof data.dateFin === 'string') {
            endDate = new Date(data.dateFin);
          } else if (data.dateFin.seconds) {
            endDate = new Date(data.dateFin.seconds * 1000);
          }


          if (!startDate || !endDate || isNaN(startDate) || isNaN(endDate)) {
            return null;
          }

          // Normaliser les dates pour la comparaison
          const normalizedEndDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          const normalizedStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          
          return {
            id: doc.id,
            ...data,
            userName: data.nomComplet || `${data.nomPersonnel || ''} ${data.prenomPersonnel || ''}`.trim() || 'Personnel inconnu',
            startDate: normalizedStartDate,
            endDate: normalizedEndDate,
            reason: data.motif || '',
            status: data.statut || 'en attente'
          };
        } catch (error) {
          console.error('Erreur lors du traitement de la date pour l\'absence:', doc.id, error);
          return null;
        }
      }).filter(Boolean);


      // Filtrer les absences en cours
      const absences = allAbsences.filter(absence => {

        // Inclure les absences signalées et approuvées qui ne sont pas terminées
        return (absence.status === 'approuvé' || absence.status === 'signalée') && 
               absence.endDate >= today;
      });

      // Filtrer les demandes en attente
      const requests = allAbsences.filter(absence => 
        absence.status === 'en attente' && absence.endDate >= today
      );


      setCurrentAbsences(absences);
      setPendingRequests(requests);
    } catch (error) {
      console.error('Erreur lors de la récupération des absences:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchAbsencesAndRequests();
      // Mettre à jour toutes les 5 minutes
      const interval = setInterval(fetchAbsencesAndRequests, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const AbsenceCard = ({ title, items, type }) => (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Badge badgeContent={items.length} color="primary">
            <EventIcon />
          </Badge>
        </Box>
        <List>
          {items.length === 0 ? (
            <ListItem>
              <ListItemText 
                primary="Aucun élément à afficher"
                secondary="Tout est à jour"
              />
            </ListItem>
          ) : (
            items.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    type === 'request' && (
                      <Chip
                        label={getStatusLabel(item.status)}
                        color={getStatusColor(item.status)}
                        size="small"
                      />
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={item.userPhoto}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.userName}
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {formatDate(item.startDate)}
                          {' - '}
                          {formatDate(item.endDate)}
                        </Typography>
                        <br />
                        {item.reason}
                      </React.Fragment>
                    }
                  />
                </ListItem>
                {index < items.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))
          )}
        </List>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AbsenceCard
            title="Absences en cours"
            items={currentAbsences}
            type="absence"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AbsenceCard
            title="Demandes en attente"
            items={pendingRequests}
            type="request"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AbsenceOverview;
